import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { useLoadingScreen } from '../../Store/LoadingManager';
import QueryBuilderHelper from "../../Utilities/Helpers/QueryBuilderHelper";
import { DeleteCompanyUserService, CreateCompanyUserService, EditCompanyUserService, GetCompanyById, GetCompanyUsers, SyncCompanyUserService } from "../../Api/Service/Company/companyService";
import { useLocation } from "react-router-dom";
import { ErrorMessage, SuccessMessage } from "../../Constants/Messages";

export function useCompanyUsersHook(q_queryKey: any, params: any, runInitialFetch: boolean = true) {
    let queryClient = useQueryClient();
    // let { id: companyid } = useParams();
    let location = useLocation();
    let queryParams = new URLSearchParams(location.search);
    let companyid = queryParams.get('id') ?? "0";
    let { loading, setLoading } = useLoadingScreen();

    let { enqueueSnackbar } = useSnackbar();
    let fileBaseUrl = `${process.env.REACT_APP_API_URL}/api/`;

    // Initial fetch
    const { data, fetchStatus } = useQuery({
        queryKey: q_queryKey,
        queryFn: () => getCompanyUsersList(params),
        enabled: runInitialFetch,
    });

    const getCompanyUsersList = async (params: any) => {
        let _data;
        const responseData = await GetCompanyUsers(companyid ?? "", QueryBuilderHelper.BuildQueryString(params));
        if (responseData?.isSuccess) {
            _data = responseData?.data
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }
        return _data;
    }

    const getCompanyById = async (id: string | number) => {
        let _data;
        const responseData = await GetCompanyById(id);
        
        return responseData?.data ?? null;
    }

    const createCompanyUser = async (params: any) => {
        setLoading(true);

        const responseData = await CreateCompanyUserService(companyid, params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Company " + SuccessMessage.CREATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_CREATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const editCompanyUserById = async (id: any, params: any) => {
        setLoading(true);
        const responseData = await EditCompanyUserService(companyid ?? "", id, params);
        
        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey)
            enqueueSnackbar(responseData?.data?.message ?? "User " + SuccessMessage.UPDATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_UPDATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const deleteCompanyUserById = async (id: any) => {
        setLoading(true);

        const responseData = await DeleteCompanyUserService(companyid ?? "", id);
        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey)
            enqueueSnackbar(responseData?.data?.message ?? "User " + SuccessMessage.DELETED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_DELETING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const syncCompanyUser = async (params: any) => {
        setLoading(true);

        const responseData = await SyncCompanyUserService(companyid, params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? SuccessMessage.SYNC_COMPANY_USERS_SUCESSFULLY_SYNCHED, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_SYNCHING_COMPANY_USERS), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }
    
    const companyUserInvalidateQueries = async () => {
        queryClient.invalidateQueries(q_queryKey);
        return true;
    }

    return { data, fetchStatus, createCompanyUser, editCompanyUserById, deleteCompanyUserById, getCompanyById, companyUserInvalidateQueries, syncCompanyUser }
}